import { useEffect, useMemo, useRef, useState } from 'react';
import { Container as pixiContainer, Resource, Texture } from 'pixi.js';
import { Container, Sprite } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import gsap from 'gsap';

import { JackPotSceneItemGraphics } from '../jackPotSceneItemGraphic';
import { getGameImages } from '../../../../../helpers';
import { isMobile } from '../../../../../../helpers';
import { Spine } from '../../../../../components';
import { useStore } from '../../../../../../hooks';
import { toJS } from 'mobx';

export const JackPotSceneItemContent: React.FC<any> = observer(({
  x = 0,
  y = 0,
  winCard,
  itemIndex,
  setWinCard,
  jackpotIcons,
  jackPotItemEnum,
  jackpotSceneItemInfo,
  symbolMoveContainerRef,
}) => {
  const jackPotSceneItemBoxRef: any = useRef(null);
  const jackPotSceneItemSymbolRef: any = useRef(null);
  const jackPotSceneItemWinSymbolRef: any = useRef(null);
  const [spineBox, setSpineBox] = useState<any | null>(null);
  const [spineSymbol, setSpineSymbol] = useState<any | null>(null);
  const [sceneSymbolForWin, setSceneSymbolForWin] = useState<any | null>(null);
  const { clubsImage, dimondImage, heartImage, spadesImage } = getGameImages();
  const symbolImages = [clubsImage, dimondImage, heartImage, spadesImage];
  const { isWin, jackpotCard, index, isEnd } = jackpotSceneItemInfo || {};
  const [image, setImage] = useState<Texture<Resource>>(clubsImage);
  const [imageVisible, setImageVisible] = useState<boolean>(false);
  const { jackpotStore } = useStore();
  const {
    isExpiredJackpotCards,
    isAllowDoJackpotAction,
    setOpenedJackpotCardCount,
    openedJackpotCardCount,
    setIsAllowDoJackpotAction,
    setIsWinMoveAndScaleSymbolComplete,
  } = jackpotStore;

  const skinName = useMemo(() => jackpotIcons[jackpotCard] || '', [jackpotIcons, jackpotCard]);
  const isOpened = useMemo(() => {
    const val = index === itemIndex
    return val;
  }, [index, itemIndex]);

  useEffect(() => {
    const itemSymbol: any = new Spine({ key: 'jackPotSymbol' });
    const itemSymbolForWin = new Spine({ key: 'jackPotSymbol' });
    itemSymbol.play('idle', true);
    const itemBox: any = new Spine({ key: 'jackPotItem' });
    itemBox.isMoved = false;
    itemSymbol.isMoved = false;
    jackPotSceneItemWinSymbolRef.current.addChild(itemSymbolForWin);
    jackPotSceneItemSymbolRef.current.addChild(itemSymbol);
    jackPotSceneItemBoxRef.current.addChild(itemBox);
    setSpineBox(itemBox);
    setSpineSymbol(itemSymbol);
    setSceneSymbolForWin(itemSymbolForWin);
  }, []);

  const handleOpenedIconMoveAndScaleAnimation = (sprite: any, count: number) => {
    if (isExpiredJackpotCards) {
      setImageVisible(false);
      return;
    };
    const container: pixiContainer = new pixiContainer();
    container.addChild(spineSymbol);
    container.x = x;
    container.y = y;
    symbolMoveContainerRef.current.addChild(container);
    spineSymbol.play('idle');
    startOpenedAnimation(sprite, count)
  };

  const startOpenedAnimation = (sprite: any, count: number) => {
    jackPotItemEnum.forEach((item: any) => {
      const { id, positions } = item;

      if (id === jackpotCard && !sprite.isMoved) {
        const targetPosition = positions?.[count];
        if (!targetPosition) return;

        setIsAllowDoJackpotAction(true);
        setImage(symbolImages[jackpotCard - 1]);
        setImageVisible(true);
        const scale = isMobile()
          ? Math.min(window.innerWidth / 540, window.innerHeight / 960)
          : 1;
        const parentContainer = symbolMoveContainerRef.current?.parent?.parent;
        const marginX = isMobile() && parentContainer ? parentContainer.x / scale : 0;
        const marginY = isMobile() && parentContainer ? parentContainer.y / scale : 0;

        gsap.to(sprite, {
          x: targetPosition.x - sprite.getGlobalPosition().x / scale + marginX,
          y: targetPosition.y - sprite.getGlobalPosition().y / scale + marginY,
          duration: isEnd ? 0 : 0.7,
          onComplete: () => {
            sprite.play('win', false, isEnd ? 1000 : 1);
            sprite.addListener(
              'complete',
              () => {
                if (isWin) {
                  setWinCard(jackpotCard);
                  setIsWinMoveAndScaleSymbolComplete(true);
                }
              },
              null,
              'win'
            );
          },
        });
        gsap.to(sprite.scale, {
          x: 1,
          y: 1, 
          duration: isEnd ? 0 : 0.3,
          onComplete: () => {
            gsap.to(sprite.scale     , {
              x: isMobile() ? (count === 2 ? 0.4 : 0.25) : count === 2 ? 0.8 : 0.6,
              y: isMobile() ? (count === 2 ? 0.4 : 0.25) : count === 2 ? 0.8 : 0.6,
              duration: 0.3,
            });
          },
        });
      };
    });
  };

  useEffect(() => {
    if (isOpened && skinName && spineBox) {
      !spineBox.isMoved && spineBox.play('open', false, isEnd ? 10000 : 1);
      const count: number = openedJackpotCardCount[jackpotCard];
      setOpenedJackpotCardCount(jackpotCard);      
      setTimeout(() => {
        if (winCard === jackpotCard) {
          sceneSymbolForWin.skeleton.setSkinByName(skinName);
          sceneSymbolForWin.play('win', true);
          return;
        };
        // if (spineSymbol.isMoved) return;
        !spineSymbol.isMoved && spineSymbol.play('win', false, isEnd ? 10000 : 1);
        spineSymbol.skeleton.setSkinByName(skinName);
        spineSymbol.addListener(
          'complete',
          () => {
            handleOpenedIconMoveAndScaleAnimation(spineSymbol, count);
            spineBox.isMoved = true;
            spineSymbol.isMoved = true;
          },
          null,
          'win',
          true
        );
      }, 250);
    };
  }, [
    isEnd,
    isOpened,
    spineBox,
    winCard,
    spineSymbol,
    jackpotCard,
    sceneSymbolForWin,
    openedJackpotCardCount,
    symbolMoveContainerRef,
  ]);


  return (
    <Container x={x} y={y} key={itemIndex} value={itemIndex} {...({ name: 'jackPotItemSectionContainer' } as any)}>
      <Sprite texture={image} scale={0.5} anchor={0.5} visible={imageVisible} />
      <Container ref={jackPotSceneItemBoxRef} {...({ name: 'JackPotSceneItemBoxContainer' } as any)} />
      <Container ref={jackPotSceneItemWinSymbolRef} {...({ name: 'JackPotSceneItemWinSymbolContainer' } as any)} />
      <Container ref={jackPotSceneItemSymbolRef} {...({ name: 'JackPotSceneItemSymbolContainer' } as any)} />
      <JackPotSceneItemGraphics
        width={135}
        height={137}
        setIsAllowDoJackpotAction={setIsAllowDoJackpotAction}
        index={itemIndex}
        isOpened={isOpened}
        jackPotSceneItemBoxRef={jackPotSceneItemBoxRef}
        isAllowDoJackpotAction={isAllowDoJackpotAction}
      />
    </Container>
  );
});
